import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, FileText, Building2, Shield, MapPin } from 'lucide-react';

const LicensingDisclosurePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#003D5C] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/" className="inline-flex items-center text-blue-200 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Licensing & Disclosure Statement</h1>
          <p className="text-blue-200 mt-2">Regulatory information and disclosures</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          <div className="bg-[#F0F7FF] p-6 rounded-xl mb-8 border-l-4 border-[#0077B6]">
            <p className="text-gray-700 m-0">
              <strong>Dominion Lending Centres Utmost Financial</strong> is the trade name of <strong>Utmost Financial Inc.</strong> We are committed to transparency and compliance with all applicable regulatory requirements.
            </p>
          </div>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Company Information</h2>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 m-0"><strong>Legal Name:</strong> Utmost Financial Inc.</p>
              <p className="text-gray-700 m-0"><strong>Trade Name:</strong> Dominion Lending Centres Utmost Financial</p>
              <p className="text-gray-700 m-0"><strong>Head Office:</strong> A-970 Sargent Avenue, Winnipeg, MB R3E0E3</p>
              <p className="text-gray-700 m-0"><strong>Phone:</strong> 1-877-239-9060</p>
              <p className="text-gray-700 m-0"><strong>Email:</strong> hello@utmostfinancial.ca</p>
            </div>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <Award className="h-5 w-5 text-[#003D5C]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Mortgage Brokerage Licenses</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Utmost Financial Inc. is licensed as a mortgage brokerage in the following provinces:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-[#0077B6]" />
                  <h3 className="font-bold text-gray-800 m-0">Manitoba</h3>
                </div>
                <p className="text-gray-600 text-sm m-0">
                  Licensed under the Mortgage Brokers and Mortgage Administrators Act<br />
                  Regulated by: Manitoba Securities Commission
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-[#0077B6]" />
                  <h3 className="font-bold text-gray-800 m-0">Alberta</h3>
                </div>
                <p className="text-gray-600 text-sm m-0">
                  Licensed under the Real Estate Act<br />
                  Regulated by: Real Estate Council of Alberta (RECA)
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#0077B6] rounded-lg flex items-center justify-center">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Insurance Licensing</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Our insurance representatives are licensed to sell life, accident and sickness insurance products in their respective provinces. All insurance representatives are registered with:
            </p>
            <ul className="text-gray-600 space-y-2">
              <li>Insurance Council of Manitoba (ICM)</li>
              <li>Insurance Council of Saskatchewan</li>
              <li>Alberta Insurance Council (AIC)</li>
              <li>Insurance Council of British Columbia (ICBC)</li>
            </ul>
          </section>

          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#003D5C]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 m-0">Important Disclosures</h2>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[#FFF8E7] p-4 rounded-lg border border-[#D4AF37]/30">
                <h3 className="font-bold text-gray-800 mb-2">Mortgage Broker Disclosure</h3>
                <p className="text-gray-600 text-sm m-0">
                  As mortgage brokers, we work with multiple lenders to find mortgage products that may be suitable for your needs. We may receive compensation from lenders in the form of commissions or fees. We are required to disclose any potential conflicts of interest and will provide you with full details of any fees or commissions before you commit to a mortgage.
                </p>
              </div>
              
              <div className="bg-[#F0F7FF] p-4 rounded-lg border border-[#0077B6]/30">
                <h3 className="font-bold text-gray-800 mb-2">Insurance Disclosure</h3>
                <p className="text-gray-600 text-sm m-0">
                  Insurance products are offered through licensed insurance representatives. We may represent multiple insurance companies and will recommend products based on your needs. Commissions may vary by product and insurer. All coverage is subject to policy terms, conditions, and exclusions.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-2">Investment Disclosure</h3>
                <p className="text-gray-600 text-sm m-0">
                  Investment products including segregated funds and GICs are offered through licensed representatives. Past performance does not guarantee future results. All investments carry risk, and you may lose some or all of your investment. Please read the fund facts or prospectus before investing.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Network Affiliation</h2>
            <p className="text-gray-600">
              Dominion Lending Centres Utmost Financial operates as an independently owned franchise of Dominion Lending Centres, one of Canada's largest mortgage brokerage networks. Our affiliation provides access to a wide range of lenders and mortgage products.
            </p>
            <div className="mt-4">
              <a 
                href="https://www.dlcutmostfinancial.ca" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#0077B6] hover:text-[#005A8C] font-medium"
              >
                Visit DLC Utmost Financial Website →
              </a>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800">Contact for Licensing Inquiries</h2>
            <p className="text-gray-600">
              For questions about our licensing or to verify our credentials, please contact:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              <p className="text-gray-700 m-0"><strong>Compliance Department</strong></p>
              <p className="text-gray-600 m-0">Utmost Financial Inc.</p>
              <p className="text-gray-600 m-0">Email: compliance@utmostfinancial.ca</p>
              <p className="text-gray-600 m-0">Phone: 1-877-239-9060</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default LicensingDisclosurePage;
